import { memo, useMemo, useState } from "react";
import axios from "axios";
import { isClientRejectionError } from "src/helpers/apiError";
import { getApiErrorDetail } from "src/reusable/utils/toastApiError";
import UnifiedSidebar from "src/reusable/Sidebar/UnifiedSidebar";
import SidebarToggleButton from "src/reusable/Sidebar/SidebarToggleButton";
import { useResponsive } from "src/reusable/hooks/useResponsive";
import { useUIStore } from "src/views/assistant/stores/UIStore";
import { useItemEquivalencySidebar } from "../hooks/useItemEquivalencySidebar";
import { useItemEquivalencyRecord } from "../services/ItemEquivalencyService";

interface Props {
  dialogueId?: number;
  responseSources: any[];
  // From the parent page's stream store: a second useOwnChatStream mount here
  // fired a duplicate sub-agent restore request for the same dialogue.
  artifacts: Record<string, any>;
}

const TRANSIENT_REJECTION_STATUSES = new Set([408, 429]);

/** Request timeout or rate limit: a 4xx the server expects to be retried. */
const isTransientRejection = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  TRANSIENT_REJECTION_STATUSES.has(error.response?.status ?? 0);

const ItemEquivalencySidebar = memo(
  ({ dialogueId, responseSources, artifacts }: Props) => {
    const { isMobile } = useResponsive();
    const { rightSidebar, setRightSidebar } = useUIStore();
    const [viewMode, setViewMode] = useState<"dataChunk" | "cardList">(
      "dataChunk",
    );
    const {
      data: record,
      isError: recordError,
      error,
      refetch,
    } = useItemEquivalencyRecord(dialogueId);
    const recordErrorDetail = recordError
      ? getApiErrorDetail(error)
      : undefined;
    // A 4xx is the server's final answer to this request; only a network or
    // server failure can end differently on the next try. 408 and 429 are
    // the exceptions: the server asked for the request again later.
    const retryRecord =
      isClientRejectionError(error) && !isTransientRejection(error)
        ? undefined
        : refetch;

    const context = useMemo(
      () => ({
        dialogueId,
        record,
        recordError,
        recordErrorDetail,
        retryRecord,
        responseSources,
        artifacts,
        viewMode,
        setViewMode,
      }),
      [
        dialogueId,
        record,
        recordError,
        recordErrorDetail,
        retryRecord,
        responseSources,
        artifacts,
        viewMode,
      ],
    );
    const config = useItemEquivalencySidebar(context);
    const totalItems =
      1 + responseSources.length + Object.keys(artifacts).length;

    // Opening the sidebar on entry is owned by useItemEquivalencySidebar's
    // effect, which gates on the user's auto-open preference.

    return (
      <>
        <UnifiedSidebar
          config={config}
          context={context}
          open={rightSidebar.isOpen}
          onOpenChange={(isOpen) => setRightSidebar({ isOpen })}
          selectedTab={rightSidebar.selectedItem}
          onTabChange={(selectedItem) => setRightSidebar({ selectedItem })}
          width={rightSidebar.width}
          onWidthChange={(width) => setRightSidebar({ width })}
        />
        {isMobile && (
          <SidebarToggleButton
            open={rightSidebar.isOpen}
            onClick={() =>
              setRightSidebar({
                isOpen: !rightSidebar.isOpen,
                ...(!rightSidebar.isOpen && { selectedItem: "plan" }),
              })
            }
            badgeContent={totalItems}
            tooltipTitle={
              rightSidebar.isOpen ? "Close sidebar" : "View evaluation plan"
            }
            show
          />
        )}
      </>
    );
  },
);

ItemEquivalencySidebar.displayName = "ItemEquivalencySidebar";

export default ItemEquivalencySidebar;
